import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, PlusCircle, MapPin, Inbox, List, Map as MapIcon, ShieldCheck, Users, Globe2 } from "lucide-react";
import { api, CATEGORIES } from "@/lib/api";
import { PostCard } from "@/components/PostCard";
import { PostsMap } from "@/components/PostsMap";
import { useAuth } from "@/context/AuthContext";
import Communities from "@/pages/Communities";

const KINDS = [
  { key: "", label: "Everything" },
  { key: "request", label: "Needs help" },
  { key: "offer", label: "Offering help" },
];

function ModeSwitch({ mode, setMode }) {
  const tabs = [
    { k: "people", icon: Users, label: "People", sub: "Help nearby" },
    { k: "towns", icon: Globe2, label: "Towns", sub: "Your hometown" },
  ];
  return (
    <div className="mb-6" data-testid="home-mode-switch">
      <div className="inline-flex bg-white border-2 border-border rounded-full p-1.5 shadow-sm">
        {tabs.map((t) => {
          const active = mode === t.k;
          return (
            <button key={t.k} onClick={() => setMode(t.k)} data-testid={`mode-${t.k}`}
              className={`flex items-center gap-2.5 px-5 sm:px-7 py-2.5 rounded-full font-bold transition-colors ${active ? "bg-forest-900 text-white" : "text-forest-700 hover:bg-forest-900/5"}`}>
              <t.icon size={22} strokeWidth={2.5} />
              <span className="text-left leading-tight">{t.label}
                <span className={`block text-xs font-medium ${active ? "text-white/70" : "text-forest-500"}`}>{t.sub}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Feed() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);
  const [kind, setKind] = useState("");
  const [category, setCategory] = useState("");
  const [nearby, setNearby] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [q, setQ] = useState("");
  const [view, setView] = useState("list");
  const [mode, setMode] = useState("people");

  const load = useCallback(async () => {
    setPosts(null);
    const params = {};
    if (kind) params.kind = kind;
    if (category) params.category = category;
    if (nearby) params.radius = 25;
    if (verifiedOnly) params.verified_only = true;
    const { data } = await api.get("/posts", { params });
    setPosts(data);
  }, [kind, category, nearby, verifiedOnly]);

  useEffect(() => { load(); }, [load]);

  const filtered = (posts || []).filter((p) =>
    !q || p.title.toLowerCase().includes(q.toLowerCase()) || p.description.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <ModeSwitch mode={mode} setMode={setMode} />
      {mode === "towns" ? <Communities embedded /> : (
      <>
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div>
          <h1 className="font-head text-3xl sm:text-4xl font-bold text-forest-900">Hello, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-lg text-forest-700 mt-1">Find people nearby to help — or lend a hand yourself.</p>
        </div>
        <button onClick={() => navigate("/app/new")} data-testid="feed-new-post-btn"
          className="ml-auto min-h-[56px] px-7 rounded-full bg-terracotta text-white font-bold text-lg hover:bg-terracotta-dark transition-transform active:scale-95 flex items-center gap-2">
          <PlusCircle size={24} strokeWidth={2.5} /> Post something
        </button>
      </div>

      <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-4 sm:p-6 mb-6 space-y-4">
        <div className="relative">
          <Search size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest-500" strokeWidth={2.5} />
          <input data-testid="feed-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search posts…"
            className="w-full min-h-[52px] text-lg pl-12 pr-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-cream" />
        </div>
        <div className="flex flex-wrap gap-2">
          {KINDS.map((k) => (
            <button key={k.key} onClick={() => setKind(k.key)} data-testid={`filter-kind-${k.key || "all"}`}
              className={`px-4 py-2 rounded-full font-semibold border-2 transition-colors ${kind === k.key ? "bg-forest-900 text-white border-forest-900" : "bg-white text-forest-700 border-border hover:border-forest-500"}`}>
              {k.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <SlidersHorizontal size={18} className="text-forest-500" strokeWidth={2.5} />
          <button onClick={() => setCategory("")} data-testid="filter-cat-all"
            className={`px-4 py-2 rounded-full font-semibold border-2 transition-colors ${category === "" ? "bg-terracotta text-white border-terracotta" : "bg-white text-forest-700 border-border"}`}>All types</button>
          {Object.entries(CATEGORIES).map(([key, c]) => (
            <button key={key} onClick={() => setCategory(key)} data-testid={`filter-cat-${key}`}
              className={`px-4 py-2 rounded-full font-semibold border-2 transition-colors ${category === key ? "bg-terracotta text-white border-terracotta" : "bg-white text-forest-700 border-border"}`}>{c.label}</button>
          ))}
          <button onClick={() => setNearby((v) => !v)} data-testid="filter-nearby"
            className={`px-4 py-2 rounded-full font-semibold border-2 transition-colors flex items-center gap-1.5 ${nearby ? "bg-gold text-forest-900 border-gold" : "bg-white text-forest-700 border-border"}`}>
            <MapPin size={16} strokeWidth={2.5} /> Nearby (25km)
          </button>
          <button onClick={() => setVerifiedOnly((v) => !v)} data-testid="filter-verified"
            className={`px-4 py-2 rounded-full font-semibold border-2 transition-colors flex items-center gap-1.5 ${verifiedOnly ? "bg-forest-700 text-white border-forest-700" : "bg-white text-forest-700 border-border"}`}>
            <ShieldCheck size={16} strokeWidth={2.5} /> Verified only
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4" data-testid="view-toggle">
        <button onClick={() => setView("list")} data-testid="view-list"
          className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold border-2 transition-colors ${view === "list" ? "bg-forest-900 text-white border-forest-900" : "bg-white text-forest-700 border-border"}`}>
          <List size={20} strokeWidth={2.5} /> List
        </button>
        <button onClick={() => setView("map")} data-testid="view-map"
          className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold border-2 transition-colors ${view === "map" ? "bg-forest-900 text-white border-forest-900" : "bg-white text-forest-700 border-border"}`}>
          <MapIcon size={20} strokeWidth={2.5} /> Map
        </button>
        <span className="ml-auto text-forest-500 font-medium">{filtered.length} {filtered.length === 1 ? "post" : "posts"}</span>
      </div>
      {view === "map" ? (
        <PostsMap posts={filtered} onSelect={(p) => navigate(`/app/post/${p.post_id}`)} />
      ) : posts === null ? (
        <div className="grid gap-5 sm:grid-cols-2">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-56 rounded-2xl bg-white/60 border-2 border-border animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-border p-12 text-center">
          <Inbox size={48} className="mx-auto text-forest-500 mb-4" strokeWidth={2} />
          <h3 className="font-head text-2xl font-bold text-forest-900">No posts here yet</h3>
          <p className="text-forest-700 mt-2 mb-6">Be the first to reach out — post a request or an offer.</p>
          <button onClick={() => navigate("/app/new")} data-testid="empty-new-post"
            className="min-h-[56px] px-7 rounded-full bg-terracotta text-white font-bold text-lg hover:bg-terracotta-dark transition-colors">Post something</button>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2" data-testid="feed-list">
          {filtered.map((p) => <PostCard key={p.post_id} post={p} />)}
        </div>
      )}
      </>
      )}
    </div>
  );
}
